import React, { Fragment, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlusOutlined } from '@ant-design/icons'

import { ExtendedTable, SubheaderToolbarDivider } from '@app/components'
import { LntButton } from '@app/components/base'
import { TableSearchButton } from '@app/components/TableSettingsToolbar'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TRANSLATIONS_CONFIG_KEY } from '@app/constants/queryKeys'

import { useNotification, usePaginationParams, useRemoveTableRowButton } from '@app/hooks'
import { getTranslationsConfigCompetitorsDataFromResponse } from '@app/utils/responseAdapters'

import {
  deleteTranslationsConfigCompetitor,
  getTranslationsConfigCompetitors
} from '@app/api/routes/translationsConfig'

import { getColumns } from './utils'

import type { CompetitorsTabProps } from './types'
import type { TranslationsConfigCompetitorsDataSource } from '@app/types/dataSource/TranslationsConfigDataSource'

const columns = getColumns()

export const CompetitorsTab: React.FC<CompetitorsTabProps> = props => {
  const { onOpenAddListCompetitorsDrawer } = props

  const queryClient = useQueryClient()
  const notification = useNotification()

  const { pagination, setPagination, paginationParams } = usePaginationParams()

  const { data: translationsCompetitorsData, isLoading: isTranslationsCompetitorsDataLoading } = useQuery({
    queryKey: [TRANSLATIONS_CONFIG_KEY.competitors],
    queryFn: async () => await getTranslationsConfigCompetitors({ ...paginationParams })
  })

  const deleteTranslationsCompetitorsMutation = useMutation({
    mutationFn: deleteTranslationsConfigCompetitor,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [TRANSLATIONS_CONFIG_KEY.competitors] })

      notification.success({
        message: 'Выбранный магазин удален из транслирования.'
      })
    }
  })

  const dataSource = useMemo(
    () => getTranslationsConfigCompetitorsDataFromResponse(translationsCompetitorsData),
    [translationsCompetitorsData]
  )

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleDeleteCompetitor = (rowKey: string): void => {
    deleteTranslationsCompetitorsMutation.mutate({ competitorSapId: rowKey })
  }

  useRemoveTableRowButton({
    columns,
    onClick: rowKey => handleDeleteCompetitor(String(rowKey))
  })

  const toolbar = [
    <Fragment key='add-competitor'>
      <LntButton
        icon={ <PlusOutlined /> }
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ onOpenAddListCompetitorsDrawer }
      >
        Магазин
      </LntButton>

      <SubheaderToolbarDivider />
    </Fragment>,

    // TODO: Добавить табличный фильтр
    <TableSearchButton key='search-competitors' />
  ]

  return (
    <ExtendedTable<TranslationsConfigCompetitorsDataSource>
      loading={ isTranslationsCompetitorsDataLoading }
      toolbar={ toolbar }
      columns={ columns }
      dataSource={ dataSource }
      pagination={ {
        ...pagination,
        total: translationsCompetitorsData?.count,
        onChange: handlePaginationChange
      } }
    />
  )
}
